import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Feather, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import UpgradeGate from "../components/UpgradeGate";

const RANK = { free: 0, mid: 1, premium: 2 };

export default function Journal() {
  const { profile, user } = useAuth();
  const pid = profile?.id;
  const [entries, setEntries] = useState([]);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [reflecting, setReflecting] = useState(null);

  useEffect(() => {
    if (!pid) return;
    api.get(`/profiles/${pid}/journal`).then(({ data }) => setEntries(data));
  }, [pid]);

  if (profile === null) return <Navigate to="/setup" replace />;
  if (user && RANK[user.tier || "free"] < RANK.mid)
    return <Shell><UpgradeGate requiredTier="mid" feature="Journaling" /></Shell>;

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/profiles/${pid}/journal`, { content: content.trim() });
      setEntries((e) => [data, ...e]);
      setContent("");
      toast.success("Entry saved. Your companion will read this.");
    } catch { toast.error("Could not save entry"); } finally { setSaving(false); }
  };

  const reflect = async (id) => {
    setReflecting(id);
    try {
      const { data } = await api.post(`/profiles/${pid}/journal/${id}/reflect`);
      setEntries((e) => e.map((x) => x.id === id ? { ...x, reflection: data.reflection } : x));
    } catch { toast.error("Reflection failed"); } finally { setReflecting(null); }
  };

  return (
    <Shell>
      <p className="overline mb-2 flex items-center gap-2"><Feather size={14} /> Journal</p>
      <h1 className="font-serif text-5xl mb-8">Write it out. It listens.</h1>

      <div className="panel p-6 mb-8">
        <textarea data-testid="journal-input" value={content} onChange={(e) => setContent(e.target.value)}
          rows={5} placeholder="What's alive for you today?"
          className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none resize-none" />
        <div className="flex justify-end mt-3">
          <button data-testid="journal-save" onClick={save} disabled={saving}
            className="gold-btn px-6 py-2.5 rounded-full font-medium flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Feather size={16} />} Save entry
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {entries.length ? entries.map((e) => (
          <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`journal-entry-${e.id}`} className="panel p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{new Date(e.created_at).toLocaleString()}</p>
            <p className="text-foreground/90 whitespace-pre-wrap leading-relaxed mb-4">{e.content}</p>
            {e.reflection ? (
              <div className="border-l-2 border-primary/50 pl-4 mt-4">
                <p className="overline mb-1 flex items-center gap-2"><Sparkles size={12} /> Companion's reflection</p>
                <p className="text-sm text-foreground/90 italic leading-relaxed">{e.reflection}</p>
              </div>
            ) : (
              <button data-testid={`reflect-${e.id}`} onClick={() => reflect(e.id)} disabled={reflecting === e.id}
                className="text-sm text-primary flex items-center gap-1.5 hover:underline disabled:opacity-50">
                {reflecting === e.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Ask companion to reflect
              </button>
            )}
          </motion.div>
        )) : <p className="text-sm text-muted-foreground">No entries yet. Your reflections will build the memory your companion draws on.</p>}
      </div>
    </Shell>
  );
}
